import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  HiXMark,
  HiArrowUpRight,
  HiLink,
  HiCalendarDays,
  HiTrash,
  HiHandRaised,
  HiClock,
} from "react-icons/hi2";
import {
  sessionStatusChipClass,
  STATUS_CHIP_BASE,
} from "../../utils/sessionStatusStyles";
import { Session } from "../../types/post";
import SessionActivitiesTable from "./SessionActivitiesTable";
import { SessionNotes } from "./SessionNotesMarkdown";
import { useActivityNameResolver } from "../../hooks/useActivityNameResolver";
import ConfirmDialog from "../ConfirmDialog";
import { useOverlayStore } from "../../stores/overlayStore";
import {
  getDateChangeBlockedReason,
  getSessionSheetActions,
  getSessionDurationMinutes,
} from "../../utils/sessionRules";

// Peak Design System (Figma 2Xgaeen6KRSnprkmMAc2Fy): no session-sheet frame
// existed at the time of writing; layout follows the PrivacyBottomSheet shell
// with DaisyUI semantic tokens. Record the node id here once designed.

export const SESSION_SHEET_Z_INDEX_CLASS = "z-[900]";

const REJECT_QUICK_REASONS = [
  "Injured",
  "Schedule conflict",
  "Feeling fatigued",
  "Other",
];

export interface SessionBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  session: (Session & { id: string }) | null;
  /** Resolved display title (session name, training type or fallback). */
  title: string;
  /** Resolved training type name, when different from the title. */
  typeName?: string | null;
  /** Owner of the session (for activity-name resolution). */
  ownerUserId?: string;
  /** False when viewing another athlete: the sheet is read-only. */
  canEdit?: boolean;
  /** Whether wearable activity data is attached (blocks completed moves). */
  hasWearableActivity?: boolean;
  onOpenSession: (session: Session & { id: string }) => void;
  onLinkToLog?: (session: Session & { id: string }) => void;
  onChangeDate?: (session: Session & { id: string }) => void;
  onDelete?: (session: Session & { id: string }) => Promise<void> | void;
  onReject?: (
    session: Session & { id: string },
    reason: string,
  ) => Promise<void> | void;
}

const formatSheetDate = (plannedDate: string): string => {
  const [y, m, d] = plannedDate.split("-").map(Number);
  if (!y || !m || !d) return plannedDate;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

const formatTime = (iso?: string): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusLabel: Record<Session["status"], string> = {
  proposed: "Proposed",
  planned: "Planned",
  executing: "In progress",
  complete: "Completed",
  rejected: "Rejected",
};

/**
 * Bottom sheet for a session tapped on the day view (plan 3b/3c): title,
 * date/time, coach notes and a read-only activities summary, with Open /
 * Link / Change date / Delete-or-Reject actions decided by
 * `getSessionSheetActions`.
 */
const SessionBottomSheet: React.FC<SessionBottomSheetProps> = ({
  isOpen,
  onClose,
  session,
  title,
  typeName,
  ownerUserId,
  canEdit = true,
  hasWearableActivity = false,
  onOpenSession,
  onLinkToLog,
  onChangeDate,
  onDelete,
  onReject,
}) => {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const registerBottomSheet = useOverlayStore((s) => s.registerBottomSheet);
  const unregisterBottomSheet = useOverlayStore(
    (s) => s.unregisterBottomSheet,
  );

  // Let app-level prompts (PWA install) stay hidden while the sheet is open.
  useEffect(() => {
    if (!isOpen) return;
    registerBottomSheet();
    return () => unregisterBottomSheet();
  }, [isOpen, registerBottomSheet, unregisterBottomSheet]);

  // Reset transient state whenever the sheet opens for a (new) session.
  useEffect(() => {
    if (isOpen) {
      setConfirmingDelete(false);
      setRejecting(false);
      setRejectReason("");
      setBusy(false);
      setActionError(null);
    }
  }, [isOpen, session?.id]);

  const { getActivityName, loading: loadingActivityNames } =
    useActivityNameResolver(session?.ActivityMeasurementList, ownerUserId);

  const actions = useMemo(
    () =>
      getSessionSheetActions(session, {
        canEdit,
        hasWearableActivity,
      }),
    [session, canEdit, hasWearableActivity],
  );

  const dateChangeBlockedReason = useMemo(
    () =>
      session && canEdit && !actions.changeDate
        ? getDateChangeBlockedReason(session, { hasWearableActivity })
        : null,
    [session, canEdit, actions.changeDate, hasWearableActivity],
  );

  const measurements = session?.ActivityMeasurementList ?? [];
  const hasNotes = (session?.activityBlocks || []).some(
    (b) => b.blockType === "notes" && (b.notesContent || "").trim(),
  );
  const durationMinutes = getSessionDurationMinutes(session);
  const startLabel = formatTime(session?.startTime);
  const endLabel = formatTime(session?.endTime);
  const coachName = session?.coach?.name
    ? `${session.coach.name}${session.coach.surname ? ` ${session.coach.surname}` : ""}`
    : session?.coachID
      ? "Coach"
      : null;

  const runAction = async (fn: () => Promise<void> | void) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
    } catch (error) {
      console.error("Session sheet action failed:", error);
      setActionError(
        error instanceof Error ? error.message : "Something went wrong",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!session || !onDelete) return;
    setConfirmingDelete(false);
    await runAction(async () => {
      await onDelete(session);
      onClose();
    });
  };

  const handleSubmitReject = async () => {
    if (!session || !onReject || !rejectReason.trim()) return;
    await runAction(async () => {
      await onReject(session, rejectReason.trim());
      onClose();
    });
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && session && (
          <motion.div
            key="session-sheet-backdrop"
            className={`fixed inset-0 ${SESSION_SHEET_Z_INDEX_CLASS} bg-black/50 backdrop-blur-sm`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={busy ? undefined : onClose}
            data-testid="session-sheet-backdrop"
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="session-sheet-title"
              className="fixed bottom-0 left-0 right-0 mx-auto w-full sm:max-w-md bg-base-100 rounded-t-2xl shadow-xl flex flex-col max-h-[88vh]"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 320 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Handle bar */}
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-12 h-1 bg-base-300 rounded-full" />
              </div>

              {/* Header */}
              <div className="flex items-start justify-between gap-3 px-5 pt-1 pb-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`${STATUS_CHIP_BASE} ${sessionStatusChipClass(session.status)}`}>
                      {statusLabel[session.status]}
                    </span>
                    {typeName && typeName !== title && (
                      <span className="text-[11px] font-grotesk font-medium text-base-content/70 bg-base-200 border border-base-300 rounded-full px-2 py-0.5 max-w-[50vw] truncate">
                        {typeName}
                      </span>
                    )}
                  </div>
                  <h2
                    id="session-sheet-title"
                    className="text-lg font-heading text-base-content truncate mt-0.5"
                  >
                    {title}
                  </h2>
                  <div className="flex items-center gap-1.5 text-xs text-base-content/60 font-grotesk mt-1">
                    <HiClock className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="font-geist tabular-nums">
                      {formatSheetDate(session.plannedDate)}
                      {startLabel && <> · {startLabel}</>}
                      {startLabel && endLabel && <>–{endLabel}</>}
                      {durationMinutes > 0 && <> · {durationMinutes} min</>}
                    </span>
                  </div>
                  {coachName && (
                    <p className="text-xs text-base-content/50 font-grotesk mt-0.5">
                      Set by {coachName}
                    </p>
                  )}
                </div>
                <button
                  onClick={onClose}
                  disabled={busy}
                  className="w-11 h-11 -mt-1.5 -mr-2 flex items-center justify-center rounded-full hover:bg-base-200 transition-colors flex-shrink-0 disabled:opacity-50"
                  aria-label="Close"
                >
                  <HiXMark className="w-5 h-5 text-base-content/60" />
                </button>
              </div>

              {/* Scrollable content */}
              <div className="px-5 pb-3 overflow-y-auto space-y-4 min-h-0">
                {session.description?.trim() && (
                  <p className="text-sm text-base-content/80 font-grotesk">
                    {session.description}
                  </p>
                )}

                {hasNotes && (
                  <div className="rounded-xl bg-base-200 border border-base-300 p-3">
                    <p className="text-[11px] uppercase tracking-wider font-grotesk text-base-content/50 mb-2">
                      Coach notes
                    </p>
                    <SessionNotes blocks={session.activityBlocks} />
                  </div>
                )}

                <div className="rounded-xl bg-base-200 border border-base-300 p-3">
                  <p className="text-[11px] uppercase tracking-wider font-grotesk text-base-content/50 mb-2">
                    Activities
                    <span className="font-geist normal-case tracking-normal text-base-content/70 ml-1.5">
                      {measurements.length}
                    </span>
                  </p>
                  {measurements.length === 0 ? (
                    <p className="text-xs text-base-content/60 font-grotesk">
                      No activities recorded for this session.
                    </p>
                  ) : loadingActivityNames ? (
                    <div className="text-center py-2">
                      <span className="loading loading-spinner loading-xs" />
                    </div>
                  ) : (
                    <SessionActivitiesTable
                      measurements={measurements}
                      getActivityName={getActivityName}
                    />
                  )}
                </div>

                {rejecting && actions.reject && (
                  <div className="rounded-xl border border-error/30 bg-error/5 p-3 space-y-2">
                    <p className="text-sm font-heading text-base-content">
                      Reason for declining
                      <span className="text-error"> *</span>
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {REJECT_QUICK_REASONS.map((reason) => (
                        <button
                          key={reason}
                          type="button"
                          onClick={() => setRejectReason(reason)}
                          className={`px-3 py-2 min-h-9 text-xs font-grotesk rounded-full border transition-colors ${
                            rejectReason === reason
                              ? "bg-error text-error-content border-error"
                              : "bg-base-100 text-base-content/70 border-base-300 hover:bg-base-200"
                          }`}
                        >
                          {reason}
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Let your coach know why you're declining this session..."
                      rows={2}
                      className="w-full resize-none rounded-xl border border-base-300 bg-base-100 px-3 py-2 text-[16px] sm:text-sm text-base-content placeholder-base-content/50 focus:outline-none focus:ring-2 focus:ring-error/30 font-grotesk"
                    />
                  </div>
                )}

                {actionError && (
                  <p className="text-sm text-error font-grotesk">{actionError}</p>
                )}
              </div>

              {/* Footer actions */}
              <div className="px-5 pt-2 pb-4 border-t border-base-200 space-y-2">
                {rejecting && actions.reject ? (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setRejecting(false);
                        setRejectReason("");
                      }}
                      disabled={busy}
                      className="btn btn-ghost flex-1 min-h-11 h-auto py-3 rounded-xl bg-base-200 text-base-content font-grotesk font-semibold normal-case hover:bg-base-300 disabled:opacity-50"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmitReject}
                      disabled={busy || !rejectReason.trim()}
                      className="btn btn-error flex-1 min-h-11 h-auto py-3 rounded-xl font-grotesk font-semibold normal-case disabled:opacity-50"
                    >
                      {busy ? "Rejecting..." : "Reject session"}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => onOpenSession(session)}
                        disabled={busy}
                        className={`flex items-center justify-center gap-2 py-3 min-h-11 rounded-xl font-grotesk font-semibold transition-colors disabled:opacity-50 ${
                          actions.link
                            ? "bg-base-200 text-base-content hover:bg-base-300"
                            : "bg-primary text-primary-content hover:bg-primary/90 col-span-2"
                        }`}
                      >
                        <HiArrowUpRight className="w-4 h-4" />
                        Open session
                      </button>
                      {actions.link && onLinkToLog && (
                        <button
                          type="button"
                          onClick={() => onLinkToLog(session)}
                          disabled={busy}
                          className="flex items-center justify-center gap-2 py-3 min-h-11 rounded-xl bg-primary text-primary-content font-grotesk font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
                        >
                          <HiLink className="w-4 h-4" />
                          Link to training log
                        </button>
                      )}
                    </div>

                    {canEdit && (onChangeDate || dateChangeBlockedReason) && (
                      <button
                        type="button"
                        onClick={() =>
                          actions.changeDate && onChangeDate?.(session)
                        }
                        disabled={busy || !actions.changeDate}
                        title={dateChangeBlockedReason ?? undefined}
                        className="w-full flex items-center justify-center gap-2 py-3 min-h-11 rounded-xl border border-base-300 text-base-content font-grotesk font-medium hover:bg-base-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <HiCalendarDays className="w-4 h-4" />
                        Change date
                        {dateChangeBlockedReason && (
                          <span className="text-[11px] text-base-content/50 font-normal truncate">
                            · {dateChangeBlockedReason}
                          </span>
                        )}
                      </button>
                    )}

                    {actions.delete && onDelete && (
                      <button
                        type="button"
                        onClick={() => setConfirmingDelete(true)}
                        disabled={busy}
                        className="btn btn-outline btn-error btn-block min-h-11 h-auto py-3 rounded-xl font-grotesk font-medium normal-case gap-2 disabled:opacity-50"
                      >
                        <HiTrash className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">
                          {busy ? "Deleting..." : "Delete session"}
                        </span>
                      </button>
                    )}

                    {actions.reject && onReject && (
                      <button
                        type="button"
                        onClick={() => setRejecting(true)}
                        disabled={busy}
                        className="btn btn-outline btn-error btn-block min-h-11 h-auto py-3 rounded-xl font-grotesk font-medium normal-case gap-2 disabled:opacity-50"
                      >
                        <HiHandRaised className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">Reject session</span>
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* Safe area for mobile devices */}
              <div className="pb-safe" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmDialog
        open={confirmingDelete}
        title="Delete session?"
        message="The session and its recorded activities will be removed. Any training log stays and is unlinked."
        confirmLabel="Yes, delete"
        destructive
        loading={busy}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmingDelete(false)}
      />
    </>
  );
};

export default SessionBottomSheet;
